import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/FiveColumnWithInputForm.js";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Swal from "sweetalert2";
import { Alert } from 'react-bootstrap';
import { FaEdit, FaTrash } from "react-icons/fa";
import { useTranslation } from 'react-i18next';

const ProduitsAdmin = () => {
    const { i18n } = useTranslation();
    const { t } = useTranslation();

    const [products, setProducts] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // 🔍 NEW: Search and filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    const [newProduct, setNewProduct] = useState({
        Name: { fr: '', ar: '' },
        Description: { fr: '', ar: '' },
        user_price: '',
        distributeur_price: '',
        StockQuantity: '',
        Category: 'Cosmetique',
        image: ''
    });

    const [editingProductId, setEditingProductId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        checkAdminStatus();
    }, [i18n.language]);

    const checkAdminStatus = () => {
        const adminStatus = localStorage.getItem('isAdmin') === '1';
        setIsAdmin(adminStatus);
        if (adminStatus) {
            fetchAllProducts();
        } else {
            console.error('Unauthorized: User is not an admin');
        }
    };

    const parseIfJson = (str) => {
        if (typeof str !== 'string') return str;
        const trimmed = str.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                return JSON.parse(trimmed);
            } catch {
                return str;
            }
        }
        return str;
    };

    const fetchAllProducts = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            const response = await axios.get(`https://elanciabioapi.com/api/admin/products?lang=${i18n.language}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                const productsWithParsedFields = response.data.map(product => ({
                    ...product,
                    Name: parseIfJson(product.Name),
                    Description: parseIfJson(product.Description),
                }));
                setProducts(productsWithParsedFields);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getLangText = (field) => {
        if (!field) return '';
        if (typeof field === 'object') return field[i18n.language] || field['fr'] || '';
        return field;
    };

    // 🔍 NEW: Filtered products based on search + category
    const filteredProducts = products.filter((product) => {
        const name = getLangText(product.Name).toLowerCase();
        const matchesSearch = name.includes(searchQuery.toLowerCase());
        const matchesCategory =
            selectedCategory === 'All' || product.Category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const handleEdit = (productId) => {
        setEditingProductId(productId);
        const product = products.find(p => p.ProductID === productId);
        setNewProduct({
            Name: parseIfJson(product.Name) || { fr: '', ar: '' },
            Description: parseIfJson(product.Description) || { fr: '', ar: '' },
            user_price: product.user_price || '',
            distributeur_price: product.distributeur_price || '',
            Category: product.Category || '',
            StockQuantity: product.StockQuantity || '',
            image: product.image || ''
        });
        setShowModal(true);
    };

    const handleDelete = async (productId) => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            Swal.fire({
                title: 'Êtes-vous sûr?',
                text: "Vous ne pourrez pas revenir en arrière!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Oui, supprimer!',
                cancelButtonText: 'Annuler'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const response = await axios.delete(`https://elanciabioapi.com/api/admin/products/${productId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (response.status === 200) {
                        setProducts(products.filter(product => product.ProductID !== productId));
                        Swal.fire('Supprimé!', 'Le produit a été supprimé.', 'success');
                    }
                }
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleShowModal = () => {
        setEditingProductId(null);
        setNewProduct({
            Name: { fr: '', ar: '' },
            Description: { fr: '', ar: '' },
            user_price: '',
            distributeur_price: '',
            Category: 'Alimentaire',
            StockQuantity: '',
            image: ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleChange = (e) => {
        setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            if (!token) { setLoading(false); return; }

            const formData = new FormData();
            formData.append('Name', JSON.stringify(newProduct.Name));
            formData.append('Description', JSON.stringify(newProduct.Description));
            formData.append('user_price', newProduct.user_price);
            formData.append('distributeur_price', newProduct.distributeur_price);
            formData.append('StockQuantity', newProduct.StockQuantity);
            formData.append('Category', newProduct.Category);
            if (newProduct.image instanceof File) {
                formData.append('image', newProduct.image);
            }

            let response;
            if (editingProductId) {
                response = await axios.post(
                    `https://elanciabioapi.com/api/admin/products/${editingProductId}?_method=PUT`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
                );
            } else {
                response = await axios.post(
                    'https://elanciabioapi.com/api/admin/products',
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
                );
            }

            if (response.status === 200 || response.status === 201) {
                fetchAllProducts();
                handleCloseModal();
                Swal.fire('Succès!', `Le produit a été ${editingProductId ? 'modifié' : 'ajouté'} avec succès.`, 'success');
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    if (!isAdmin) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Alert variant="danger" style={{ width: '80%', textAlign: 'center' }}>
                    Unauthorized: You do not have admin access.
                </Alert>
            </div>
        );
    }

    // 🔍 NEW: Category buttons config
    const categories = ['All', 'Alimentaire', 'Cosmetique', 'Packs'];

    return (
        <AnimationRevealPage>
            <LightAdmin />
            <div className="d-flex flex-column">
                <section className="w-100 p-3">
                    {/* Header row: Title + Button + Search */}
<div className="d-flex justify-content-between align-items-center mb-3">
    <h6 className="text-dark display-4">{t('Tableau_de_bord_Admin')}</h6>
    <div className="d-flex align-items-center gap-2">
        <button
            className="btn btn-lg"
            style={{
                backgroundColor: '#336734',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                fontSize: '16px',
                borderRadius: '5px'
            }}
            onClick={handleShowModal}
        >
            {t('ajouter_un_produit')}
        </button>
        <input
            type="text"
            className="form-control"
            placeholder={t('Rechercher_par_nom') || 'Rechercher par nom produit'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '220px', borderRadius: '8px' }}
        />
    </div>
</div>

{/* Category filter buttons - centered */}
<div className="mb-3 d-flex flex-wrap gap-2 justify-content-center">
    {categories.map((cat) => (
        <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            style={{
                backgroundColor: selectedCategory === cat ? '#336734' : '#5a8a5e',
                color: 'white',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '8px',
                fontWeight: selectedCategory === cat ? '700' : '400',
                cursor: 'pointer',
                opacity: selectedCategory === cat ? 1 : 0.75,
                transition: 'all 0.2s ease'
            }}
        >
            {t(cat) || cat}
        </button>
    ))}
</div>

<hr className="my-4" />                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead style={{ backgroundColor: '#AFA4CE' }}>
                                <tr>
                                    <th>{t('ID_du_produit')}</th>
                                    <th>{t('Nom')}</th>
                                    <th>{t('Description')}</th>
                                    <th>{t('Quantité_en_stock')}</th>
                                    <th>{t('Prix_utilisateur')}</th>
                                    <th>{t('Prix_distributeur_influenceur')}</th>
                                    <th>{t('Catégorie')}</th>
                                    <th>{t('Image')}</th>
                                    <th>{t('Actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* 🔍 NEW: Use filteredProducts instead of products */}
                                {filteredProducts.length > 0 ? (
                                    filteredProducts.map((product) => (
                                        <tr key={product.ProductID}>
                                            <td>{product.ProductID}</td>
                                            <td>{getLangText(product.Name)}</td>
                                            <td>{getLangText(product.Description)}</td>
                                            <td>{product.StockQuantity}</td>
                                            <td>{product.user_price} DH</td>
                                            <td>{product.distributeur_price} DH</td>
                                            <td>{product.Category}</td>
                                            <td>
                                                {product.image ? (
                                                    <img
                                                        src={`https://elanciabioapi.com/storage/${product.image}`}
                                                        alt={getLangText(product.Name)}
                                                        style={{ width: '50px', height: '50px' }}
                                                    />
                                                ) : ('No Image')}
                                            </td>
                                            <td>
                                                <button className="btn btn-warning btn-sm me-2" onClick={() => handleEdit(product.ProductID)}>
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    style={{ backgroundColor: 'red' }}
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleDelete(product.ProductID)}
                                                >
                                                    <FaTrash style={{ color: 'white' }} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    // 🔍 NEW: Empty state message
                                    <tr>
                                        <td colSpan="9" className="text-center text-muted py-4">
                                            {t('Aucun_produit_trouvé') || 'Aucun produit trouvé.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
            <Footer />

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingProductId ? t('Modifier_le_produit') : t('ajouter_un_produit')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group>
                            <Form.Label>{t('Nom')} (FR)</Form.Label>
                            <Form.Control
                                type="text"
                                value={newProduct.Name.fr}
                                onChange={(e) => setNewProduct((prev) => ({ ...prev, Name: { ...prev.Name, fr: e.target.value } }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Nom')} (AR)</Form.Label>
                            <Form.Control
                                type="text"
                                value={newProduct.Name.ar}
                                onChange={(e) => setNewProduct((prev) => ({ ...prev, Name: { ...prev.Name, ar: e.target.value } }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Description')} (FR)</Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                value={newProduct.Description.fr}
                                onChange={(e) => setNewProduct((prev) => ({ ...prev, Description: { ...prev.Description, fr: e.target.value } }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Description')} (AR)</Form.Label>
                            <Form.Control
                                as="textarea" rows={2}
                                value={newProduct.Description.ar}
onChange={(e) => setNewProduct((prev) => ({ ...prev, Description: { ...prev.Description, ar: e.target.value } }))}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Prix_utilisateur')}</Form.Label>
                            <Form.Control type="number" name="user_price" value={newProduct.user_price} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Prix_distributeur_influenceur')}</Form.Label>
                            <Form.Control type="number" name="distributeur_price" value={newProduct.distributeur_price} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Quantité_en_stock')}</Form.Label>
                            <Form.Control type="number" name="StockQuantity" value={newProduct.StockQuantity} onChange={handleChange} required />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>{t('Catégorie')}</Form.Label>
                            <Form.Control as="select" name="Category" value={newProduct.Category} onChange={handleChange} required>
                                <option value="Alimentaire">{t('Alimentaire')}</option>
                                <option value="Cosmetique">{t('Cosmetique')}</option>
                                <option value="Packs">{t('Packs')}</option>
                            </Form.Control>
                        </Form.Group>
                        {editingProductId && (
                            <div className="mb-3">
                                <Form.Label>{t('Image_actuelle')}</Form.Label><br />
                                {typeof newProduct.image === 'string' && (
                                    <img
                                        src={`https://elanciabioapi.com/storage/${newProduct.image}`}
                                        alt="Product"
                                        style={{ maxWidth: "150px", borderRadius: "5px", marginBottom: "10px" }}
                                    />
                                )}
                                <Form.Control type="file" name="image" onChange={(e) => setNewProduct({ ...newProduct, image: e.target.files[0] })} />
                            </div>
                        )}
                        {!editingProductId && (
                            <Form.Group>
                                <Form.Label>{t('Image')}</Form.Label>
                                <Form.Control type="file" name="image" onChange={(e) => setNewProduct({ ...newProduct, image: e.target.files[0] })} required />
                            </Form.Group>
                        )}
                        <Button variant="primary" type="submit" className="mt-3" disabled={loading}>
                            {loading ? t('Chargement...') : editingProductId ? t('Modifier') : t('Ajouter')}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </AnimationRevealPage>
    );
};

export default ProduitsAdmin;